import { type BarChartFactory, BarChart as MantineBarChart } from "@mantine/charts";
import { componentWrapper } from "../../utilities";
import styles from "./bar-chart.module.scss";
import type { IProps } from "./bar-chart.types";
import { useMemo } from "react";
import { getBarPropsRadius } from "./bar-chart.utilities";

/**
 * Bar chart component for displaying data in a bar chart format.
 * @param {object} props.mouseEvents - Mouse event handlers for chart interactions.
 * @param {object} props.barChartProps - Additional props for the bar chart configuration.
 * @param {Array} props.series - Array of series data to display in the chart.
 * @param {string} props.type - The type of bar chart.
 * @param {boolean} props.hasCustomBar - Whether the chart uses custom bar rendering.
 * @param {Array} props.data - Array of data points for the chart.
 * @returns {JSX.Element} The BarChart component.
 */
const BarChart = componentWrapper<BarChartFactory, IProps>("BarChart", (props, ref) => {
    const { mouseEvents, barChartProps, series, type, hasCustomBar, data, ...otherProps } = props;

    const combinedBarChartProps = useMemo(
        () => ({
            ...barChartProps,
            ...mouseEvents,
        }),
        [barChartProps, mouseEvents]
    );

    return (
        <MantineBarChart
            ref={ref}
            classNames={{
                root: styles["container"],
                axisLabel: styles["axis-label"],
                axis: styles["axis"],
                bar: styles["bar"],
            }}
            withTooltip={data?.length > 0}
            data={data}
            data-type={type || "default"}
            data-has-custom={hasCustomBar ? "true" : "false"}
            type={type}
            series={series}
            {...otherProps}
            barChartProps={{ barGap: 1, barCategoryGap: 8, ...combinedBarChartProps }}
            barProps={(seriesItem) => {
                return typeof props.barProps === "function"
                    ? props.barProps(seriesItem)
                    : {
                          radius: getBarPropsRadius(seriesItem, series, type || "default"),
                          className: hasCustomBar ? styles["bar--is-hidden"] : styles["bar"],
                          ...props.barProps,
                      };
            }}
        />
    );
});

export { BarChart };

// TODO:
// - Revisit this later: Colours for series to always appear in the specific order
